import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/Components/ui/input-otp";
import { makeRequest } from "@/helpers/apiWrapper";
import { useCountryStore } from "@/Store/CountryStore";
import { useAuthModalStore } from "@/Store/useAuthModalStore";
import { useUserStore } from "@/Store/UserInfoStore";
import { useMutation } from "@tanstack/react-query";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  MessageCircle,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SignUpStepOne from "./Components/SignUpStepOne";
import PhoneInput from "./PhoneInput";
import ButtonPrimary from "../buttons/Buttons";
import { useNavigate } from "react-router";
import StepFive from "./Components/StepFive";

const SignUpComponent = ({ step, setStep }) => {
  const { t } = useTranslation();
  const [showPass, setShowPass] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(null);
  const [error, setError] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("sms");
  const { ipCountry, lastCountry } = useCountryStore();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    country: "",
    password: "",
  });
  const [otp, setOTP] = useState("");
  const { setSignupToken, signupToken } = useUserStore.getState();
  const { openAuthModal } = useAuthModalStore.getState();
  const [countdown, setCountdown] = useState(120);
  const [resendNow, setResendNow] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const countries = useCountryStore((state) => state.countries);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      phone,
    }));
  }, [phone]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      country,
    }));
  }, [country]);
  useEffect(() => {
    if (step !== 4) return;
    if (countdown === 0) {
      setResendNow(true);
      return;
    }
    if (countdown > 0) {
      timerRef.current = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setResendNow(true);
    }
    return () => clearTimeout(timerRef.current);
  }, [step, countdown]);

  const passwordRequirements = [
    {
      label: t("Password must contain at least 8 characters"),
      isValid: password.length >= 8,
    },
    {
      label: t("Password must contain at least one capital letter"),
      isValid: /[A-Z]/.test(password),
    },
    {
      label: t("Password must contain at least one small letter"),
      isValid: /[a-z]/.test(password),
    },
    {
      label: t("Password must contain at least one digit"),
      isValid: /[0-9]/.test(password),
    },
    {
      label: t("Password must contain at least one special character"),
      isValid: /[^A-Za-z0-9]/.test(password),
    },
  ];

  const handleNext = () => {
    if (step === 1) return setIsSeller;
    if (step === 2) return handleBasicInfo();
    if (step === 3) return registerMutation.mutate();
    if (step === 4) return handleVerifyOTP();
  };
  const back = () => {
    setError("");
    setStep(step - 1);
  };
  const next = () => setStep(step + 1);

  const handleBasicInfo = () => {
    setError("");
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      console.log(formData.phone);
      setError(t("First , last name and Phone are required"));
      return;
    }
    next();
  };

  const selectedCountry = countries.find((c) => c.alpha3 === formData.country);
  useEffect(() => {
    if (!country && (lastCountry || ipCountry)) {
      setCountry(lastCountry || ipCountry);
      setFormData((prev) => ({
        ...prev,
        country: lastCountry || ipCountry,
      }));
    }
  }, [lastCountry, ipCountry]);
  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCountry) {
        throw new Error(t("Select a country"));
      }

      if (!password || password !== confirmPassword) {
        throw new Error(t("Passwords must match"));
      }

      const payload = {
        phone: formData.phone,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        country_iso_code: selectedCountry.alpha3,
        country_name: selectedCountry.name,
        country: selectedCountry.id,
        isSeller: isSeller,
        isEcommerceUser: true,
        platform: selectedPlatform,
      };

      return makeRequest({
        endpoint: "/api/account/registration/individual",
        payload,
        method: "POST",
        isPublic: true,
        server: "main",
        isEncrypted: true,
      });
    },

    onSuccess: (response) => {
      setSignupToken(response.token);
      setCountdown(120);
      setError("")
      next();
    },

    onError: (error: any) => {
      const combinedMessage = `${error?.message}${error?.errorId ? ` — ${error.errorId}` : ""
        }`;
      setError(combinedMessage);
    },
  });
  const verifyOTPMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCountry) {
        throw new Error("Select a country");
      }

      if (!password || password !== confirmPassword) {
        throw new Error("Passwords must match");
      }

      const payload = {
        token: signupToken,
        sms_otp: otp,
      };

      return makeRequest({
        endpoint: "/api/account/verify-registration/individual",
        payload,
        method: "POST",
        isPublic: true,
        server: "main",
        isEncrypted: true,
      });
    },

    onSuccess: () => {
      next();
    },

    onError: (error: any) => {
      const combinedMessage = `${error?.message}${error?.errorId ? ` — ${error.errorId}` : ""
        }`;
      setError(combinedMessage);
    },
  });
  const resendOTPMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCountry) {
        throw new Error(t("Select a country"));
      }

      if (!password || password !== confirmPassword) {
        throw new Error(t("Passwords must match"));
      }

      const payload = {
        phone: formData.phone,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        country_iso_code: selectedCountry.alpha3,
        country_name: selectedCountry.name,
        country: selectedCountry.id,
        platform: selectedPlatform,
      };

      return makeRequest({
        endpoint: "/api/account/registration/individual",
        payload,
        method: "POST",
        isPublic: true,
        server: "main",
        isEncrypted: true,
      });
    },

    onSuccess: (response) => {
      setSignupToken(response.token);
    },

    onError: (error: any) => {
      const combinedMessage = `${error?.message}${error?.errorId ? ` — ${error.errorId}` : ""
        }`;
      setError(combinedMessage);
    },
  });
  const handleVerifyOTP = () => {
    setError("");
    verifyOTPMutation.mutate();
  };

  const loading =
    registerMutation.isPending ||
    verifyOTPMutation.isPending ||
    resendOTPMutation.isPending;
  return (
    <div
      className=" w-full   lg:px-10 px-0  flex 
    flex-col items-center "
    >
      <form
        className={`${step === 1 ? "lg:w-[100%] " : "lg:w-[70%]"} w-[90%] flex flex-col gap-4`}
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading) handleNext();
        }}
      >
        <div className="w-full min-h-screen flex items-start mt-4 justify-center ">
          <div className="w-full lg:w-[50%] bg-white rounded-[12px] shadow-[0_3px_8px_0_rgba(0_0_0_/_0.05)] lg:px-10 px-2 py-8 max-w-[720px]  flex flex-col gap-4">
            {step !== 5 && (
              <div className="text-center mb-4">
                <h2 className="text-[#0C0A20] text-[20px] font-bold">
                  {step === 1 ? t("Select your type") : t("Create your account")}
                </h2>
                <p className="text-[#6D6C79] text-[12px] mt-1">
                  {step === 1 &&
                    t(
                      "Choose whether you want to register as a customer or as a seller",
                    )}
                  {step === 2 && t("Please complete the form below")}
                  {step === 3 && t("Please create a strong password")}
                  {step === 4 &&
                    t("Check your phone, we’ve sent you an OTP. Paste it here")}
                </p>
              </div>
            )}
            {step === 1 && (
              <SignUpStepOne
                next={next}
                setIsSeller={setIsSeller}
                setStep={setStep}
              />
            )}

            {step === 2 && (
              <>
                <label htmlFor="" className="flex flex-col gap-1 font-semibold">
                  {t("First Name")}
                  <input
                    type="text"
                    placeholder={t("First Name")}
                    className="w-full border border-[#ddd] rounded-[6px] py-3 px-4 text-sm outline-none"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                  />
                </label>
                <label htmlFor="" className="flex flex-col gap-1 font-semibold">
                  {t("Last Name")}
                  <input
                    type="text"
                    placeholder={t("Last Name")}
                    className="w-full border border-[#ddd] rounded-[6px] py-3 px-4 text-sm outline-none"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                  />
                </label>
                <label htmlFor="" className="flex flex-col gap-1 font-semibold">
                  {t("Phone Number")}
                  <PhoneInput
                    country={
                      country ? country : lastCountry || ipCountry || "USA"
                    }
                    setCountry={setCountry}
                    phone={phone}
                    setPhone={setPhone}
                    error={error}
                    inputStyles={{}}
                    setError={() => { }}
                  />
                </label>
              </>
            )}

            {step === 3 && (
              <div className="mt-2 mb-4 relative flex flex-col gap-3">
                <div className="relative z-0">
                  <input
                    required
                    className="w-full border border-[#ddd] rounded-[6px] py-3 px-4 text-sm outline-none"
                    type={showPass ? "text" : "password"}
                    placeholder={t("Password")}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormData({ ...formData, password: e.target.value });
                    }}
                  />
                  {showPass ? (
                    <EyeOff
                      size={20}
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "24px",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowPass(!showPass)}
                    />
                  ) : (
                    <Eye
                      size={20}
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "24px",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowPass(!showPass)}
                    />
                  )}
                </div>

                <div className="relative z-0">
                  <input
                    required
                    className="w-full border border-[#ddd] rounded-[6px] py-3 px-4 text-sm outline-none"
                    type={showPass ? "text" : "password"}
                    placeholder={t("Confirm Password")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                <h5>{t("Password Requirements")}</h5>
                {passwordRequirements.map((item) => (
                  <p
                    key={item.label}
                    style={{
                      marginBottom: 0,
                      color: item.isValid ? "green" : "red",
                      fontSize: ".7rem",
                      display: "flex",
                      alignItems: "center",
                      gap: ".4rem",
                    }}
                  >
                    {item.isValid ? <Check size={16} /> : <X size={16} />}
                    {item.label}
                  </p>
                ))}

                <p
                  style={{
                    marginBottom: 0,
                    color:
                      password === confirmPassword && password.length
                        ? "green"
                        : "red",
                    fontSize: ".7rem",
                    display: "flex",
                    alignItems: "center",
                    gap: ".4rem",
                  }}
                >
                  {password === confirmPassword && password.length ? (
                    <Check size={16} />
                  ) : (
                    <X size={16} />
                  )}
                  {password === confirmPassword && password.length
                    ? t("Password matched.")
                    : t("Password doesn't match.")}
                </p>
              </div>
            )}

            {step === 4 && (
              <div
                className=" w-full flex items-center justify-center flex-col gap-3
          "
              >
                <InputOTP
                  maxLength={6}
                  className="gap-2"
                  containerClassName="flex justify-center"
                  value={otp}
                  onChange={(value) => setOTP(value)}
                >
                  <InputOTPGroup className="">
                    <InputOTPSlot className="" index={0} />
                    <InputOTPSlot className="" index={1} />
                    <InputOTPSlot className="" index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup className="">
                    <InputOTPSlot className="" index={3} />
                    <InputOTPSlot className="" index={4} />
                    <InputOTPSlot className="" index={5} />
                  </InputOTPGroup>
                </InputOTP>
                <div className="relative flex flex-col items-center justify-center gap-4 w-full">
                  {!resendNow && (
                    <p className="text-sm text-gray-500 text-center">
                      {t("Resend in")}{" "}
                      <span className="font-semibold">{countdown}s</span>
                    </p>
                  )}

                  <button
                    type="button"
                    disabled={!resendNow}
                    onClick={() => {
                      const nextPlatform =
                        selectedPlatform === "sms" ? "whatsapp" : "sms";

                      setSelectedPlatform(nextPlatform);
                      setCountdown(120);
                      setResendNow(false);
                      resendOTPMutation.mutate();
                    }}
                    className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-md text-sm
      ${resendNow
                        ? "border-gray-300 hover:bg-gray-100"
                        : "border-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    {selectedPlatform === "sms" ? (
                      <>
                        <MessageCircle size={18} />
                        <span>{t("Send via WhatsApp")}</span>
                      </>
                    ) : (
                      <>
                        <span>📩</span>
                        <span>{t("Send via SMS")}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            {step === 5 && <StepFive navigate={navigate} />}

            {error && (
              <p className="text-center text-red-500 text-xs font-medium">
                {error}
              </p>
            )}
            {step !== 1 && step !== 5 && (
              <div
                className={`w-full flex ${step > 1 ? "justify-between" : "justify-center"
                  } items-center`}
              >
                {step > 1 && (
                  <button
                    type="button"
                    onClick={back}
                    className="underline flex items-center cursor-pointer"
                  >
                    <ChevronLeft /> {t("Back")}
                  </button>
                )}
                <div className={step === 1 ? "w-full" : "w-auto"}>
                  <ButtonPrimary
                    disabled={
                      registerMutation.isPending &&
                      verifyOTPMutation.isPending &&
                      resendOTPMutation.isPending
                    }
                    type="submit"
                    text={loading ? "Please wait..." : "Continue"}
                    variant="primary"
                    iconR={<ChevronRight />}
                  />
                </div>
              </div>
            )}

            {step !== 3 && step !== 4 && step !== 5 && step !== 1 && (
              <>
                <div className="text-center text-[0.9rem] text-[#8E8E8E]">
                  {t("If you  have an account .")}
                  <span
                    onClick={() => navigate("/login")}
                    className="cursor-pointer text-[#3555f9] ml-1 underline font-semibold"
                  >
                    {t("Login")}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SignUpComponent;
